// src/pages/VerifyEmail.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Paper, Alert } from '@mui/material';
import { CheckCircle, Error, AccessTime } from '@mui/icons-material';
import { useAuth } from '../context/authContext';
import api from '../api/axios';
import { BrandLogo } from '../branding/BrandAsset';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [email, setEmail] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    // Get email from localStorage if available
    const storedEmail = localStorage.getItem('verificationEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }

    // If already authenticated, go to dashboard
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      setErrorCode('MISSING_TOKEN');
      setErrorDetails('The verification link is missing a token. Please check your email link.');
      setShowResend(true);
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await api.get(`/auth/verify/${token}`);
        
        // Check if the response is success (including already verified)
        if (response.data.success) {
          // Check if it's the "already verified" case
          if (response.data.data?.alreadyVerified) {
            setStatus('success');
            setMessage('Your email is already verified! 🎉');
            setErrorCode('');
            setErrorDetails('');
            setShowResend(false);
            localStorage.removeItem('verificationEmail');
            
            // Start countdown timer
            let count = 3;
            setCountdown(count);
            const timer = setInterval(() => {
              count -= 1;
              setCountdown(count);
              if (count <= 0) {
                clearInterval(timer);
                navigate('/login', { 
                  state: { 
                    message: 'Email already verified! Please login to continue.',
                    severity: 'success'
                  } 
                });
              }
            }, 1000);
            
            return () => clearInterval(timer);
          }
          
          // Regular success - first time verification
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully! 🎉');
          setErrorCode('');
          setErrorDetails('');
          setShowResend(false);
          
          // Clear verification email from storage
          localStorage.removeItem('verificationEmail');
          
          // Start countdown timer
          let count = 3;
          setCountdown(count);
          const timer = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              navigate('/login', { 
                state: { 
                  message: 'Email verified successfully! Please login to continue.',
                  severity: 'success'
                } 
              });
            }
          }, 1000);
          
          return () => clearInterval(timer);
        }
        
        // This should not happen if backend returns success: true, but handle just in case
        setStatus('error');
        setMessage(response.data.message || 'Verification failed');
        setErrorCode(response.data.error || 'UNKNOWN_ERROR');
        setErrorDetails(response.data.details || '');
        setShowResend(true);
        
      } catch (error) {
        console.error('Verification error:', error);
        
        const errorData = error.response?.data;
        const errorCode = errorData?.error || 'UNKNOWN_ERROR';
        
        // Check if it's an "already verified" success case from error response (fallback)
        if (errorData?.data?.alreadyVerified || errorData?.success === true) {
          setStatus('success');
          setMessage('Your email is already verified! 🎉');
          setErrorCode('');
          setErrorDetails('');
          setShowResend(false);
          localStorage.removeItem('verificationEmail');
          
          // Start countdown timer
          let count = 3;
          setCountdown(count);
          const timer = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              navigate('/login', { 
                state: { 
                  message: 'Email already verified! Please login to continue.',
                  severity: 'success'
                } 
              });
            }
          }, 1000);
          
          return () => clearInterval(timer);
        }
        
        // Actual error
        setStatus('error');
        setMessage(errorData?.message || 'Verification failed. Please try again.');
        setErrorCode(errorCode);
        setErrorDetails(errorData?.details || '');
        setShowResend(true);
      }
    };

    verifyEmail();
  }, [token, navigate, isAuthenticated]);

  const handleResendVerification = async () => {
    const userEmail = email || localStorage.getItem('verificationEmail') || prompt('Please enter your email address:');
    
    if (!userEmail) {
      setResendMessage('Email is required');
      return;
    }

    setResending(true);
    setResendMessage('');

    try {
      const response = await api.post('/auth/resend-verification', { email: userEmail });
      
      // Check for already verified (success response)
      if (response.data.success && response.data.data?.alreadyVerified) {
        setResendMessage('Your email is already verified! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email already verified! Please login to continue.',
              severity: 'success'
            } 
          });
        }, 2000);
        return;
      }
      
      // Check for already verified in error response (fallback)
      if (response.data.data?.alreadyVerified) {
        setResendMessage('Your email is already verified! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email already verified! Please login to continue.',
              severity: 'success'
            } 
          });
        }, 2000);
        return;
      }
      
      setResendMessage(response.data.message || 'Verification email sent successfully! Please check your inbox.');
      localStorage.setItem('verificationEmail', userEmail);
      setEmail(userEmail);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.data?.alreadyVerified || errorData?.success === true) {
        setResendMessage('Your email is already verified! Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email already verified! Please login to continue.',
              severity: 'success'
            } 
          });
        }, 2000);
        return;
      }
      setResendMessage(errorData?.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: '#f8f9fa',
          padding: 2,
        }}
      >
        <CircularProgress sx={{ color: '#000080', mb: 3 }} size={60} />
        <Typography variant="h6" sx={{ color: '#333' }}>
          Verifying your email...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 480,
          width: '100%',
          padding: { xs: 3, sm: 4 },
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 3 }}>
          <BrandLogo alt="ProTradesInsight" style={{ height: 50, maxWidth: '100%', objectFit: 'contain' }} />
        </Box>

        {/* Status Icon */}
        <Box sx={{ mb: 3 }}>
          {status === 'success' ? (
            <CheckCircle sx={{ fontSize: 80, color: '#2ecc71' }} />
          ) : (
            <Error sx={{ fontSize: 80, color: '#e74c3c' }} />
          )}
        </Box>

        {/* Title */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1a2e' }}>
          {status === 'success' 
            ? (message.includes('already') ? 'Already Verified! 🎉' : 'Email Verified! 🎉') 
            : 'Verification Failed'
          }
        </Typography>

        {/* Message */}
        <Typography variant="body1" sx={{ color: '#4a4a5a', mb: 2, lineHeight: 1.7 }}>
          {message}
        </Typography>

        {/* Error Details - ONLY show on actual errors, NEVER on success */}
        {status === 'error' && errorDetails && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              ℹ️ What happened:
            </Typography>
            <Typography variant="body2">
              {errorDetails}
            </Typography>
            {errorCode && (
              <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1 }}>
                Error code: {errorCode}
              </Typography>
            )}
          </Alert>
        )}

        {status === 'success' && (
          <>
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {message.includes('already') 
                ? 'Your email is already verified! Redirecting to login...' 
                : `Redirecting to login in ${countdown} seconds...`}
            </Alert>
            
            <Button
              variant="contained"
              onClick={handleGoToLogin}
              fullWidth
              sx={{
                backgroundColor: '#000080',
                '&:hover': { backgroundColor: '#0000a0' },
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
              }}
            >
              Go to Login Now
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {errorCode === 'TOKEN_EXPIRED' 
                ? 'Your verification link has expired. Please request a new one.' 
                : 'Please request a new verification link.'}
            </Alert>

            {showResend && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleResendVerification}
                  disabled={resending}
                  fullWidth
                  sx={{
                    backgroundColor: '#000080',
                    '&:hover': { backgroundColor: '#0000a0' },
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 600,
                  }}
                >
                  {resending ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                {resendMessage && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: resendMessage.includes('sent') || resendMessage.includes('verified') ? '#2ecc71' : '#e74c3c',
                      fontWeight: 500,
                    }}
                  >
                    {resendMessage}
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
              <Button
                component={Link}
                to="/login"
                variant="text"
                sx={{ 
                  color: '#000080',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,128,0.05)',
                  },
                }}
              >
                Back to Login
              </Button>
              <Button
                component={Link}
                to="/"
                variant="text"
                sx={{ 
                  color: '#666',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)',
                  },
                }}
              >
                Go to Home
              </Button>
            </Box>
          </>
        )}

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #e8e8e8' }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            &copy; {new Date().getFullYear()} ProTradesInsight. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default VerifyEmail;
